import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ServicesContext = createContext({ enabled: {}, loading: true, refresh: () => {} });

/**
 * Provides the map of service-id → enabled bool for the current user.
 * Polled every 30 s so admin changes propagate quickly to client tabs.
 */
export function ServicesProvider({ children }) {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setEnabled({});
      setLoading(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/services/enabled`, { withCredentials: true });
      const map = {};
      for (const s of r.data) map[s.id] = s.enabled;
      setEnabled(map);
    } catch (err) {
      console.warn("ServicesContext: failed to fetch /services/enabled", err);
      // assume all enabled on error so we don't accidentally block the user
      setEnabled({});
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  const value = useMemo(() => ({ enabled, loading, refresh }), [enabled, loading, refresh]);
  return (
    <ServicesContext.Provider value={value}>
      {children}
    </ServicesContext.Provider>
  );
}

export function useServices() {
  return useContext(ServicesContext);
}

/** Return true if the service is enabled. Defaults to true while loading
 *  to avoid flashing the user with "disabled" screens. */
export function useIsServiceEnabled(serviceId) {
  const { enabled, loading } = useServices();
  if (loading) return true;
  if (!(serviceId in enabled)) return true;
  return enabled[serviceId];
}
